// `z` kommt aus `@hono/zod-openapi` (Ladereihenfolge, F14); Schemas aus `@socialfy/contracts` werden unverändert eingebettet.
import { z } from '@hono/zod-openapi';
import { REMINDER_KINDS } from '@socialfy/contracts';
import { problemResponses } from '../core/headers.js';
import { CursorString, MAX_PAGE_LIMIT, PageQuery } from '../core/pagination.js';

/**
 * Schemas des Moduls `core` (TP1: Anmeldung, Sitzung, Netzwerk, Personen, Gespräche, Konten, Lücken,
 * Erinnerungen, Vorgänge, Einstellungen, Betroffenenanfragen) nach [A] 5.12 und Plan D 3.4.
 *
 * Aufzählungen mit dem Vermerk „(DB NAME)“ sind Kopien der gleichnamigen Konstanten in
 * `packages/db/src/schema/core.ts` (`api-contract` darf nicht von `db` abhängen, [V] 4.2); den
 * Gleichheitstest führt der Handler-WP W1-30.
 *
 * Optionale Felder sind `.optional()`, nicht `.exactOptional()`: der OpenAPI-Generator
 * (`@asteasolutions/zod-to-openapi`) kennt `exactOptional` nicht und führte solche Felder in `required` auf
 * (Pflicht-Parität-Test in `core.test.ts`).
 * Nullbare Komponenten (mit `.meta({ id })`) stehen als `z.union([Schema, z.null()])`: `Schema.nullable()`
 * verlöre im OpenAPI-Dokument die Nullbarkeit (ebenfalls durch den Paritätstest abgesichert).
 */

/** Wahrheitswerte kommen in der Query als Zeichenkette (F17). */
const BoolQuery = z.enum(['true', 'false']).transform((v) => v === 'true');

// ------------------------------------------------------------------------------------------------
// Gemeinsame Bausteine (auch von anderen Modulen genutzt: StrengthBand, ConnectionState)
// ------------------------------------------------------------------------------------------------

/** Stufen der Beziehungsstärke ([A] 2.5: 0–24 fleeting, 25–49 loose, 50–74 familiar, 75–100 close, null unrated). */
export const StrengthBand = z
  .enum(['fleeting', 'loose', 'familiar', 'close', 'unrated'])
  .meta({ id: 'StrengthBand' });

/** LinkedIn-Verbindung zu einer Person (DB `RELATIONSHIP_LINKEDIN_CONNECTION_VALUES`). */
export const ConnectionState = z
  .enum(['connected', 'invited', 'invited_by', 'none', 'unknown'])
  .meta({ id: 'ConnectionState' });

/** Links nach außen: nur `https://www.linkedin.com/…` (Plan D 2.3). */
export const LinkedInUrl = z.url().regex(/^https:\/\/www\.linkedin\.com\//);

/** Interner Pfad der Weboberfläche (kein absoluter URL, kein `//`). */
export const InternalPath = z.string().regex(/^\/(?!\/)[a-z0-9\-/]{0,200}$/);

/** Drittinhalt: nur als Text darstellen, nie als HTML oder Markdown ([A] 6.1.5). */
export const thirdParty = (max: number) =>
  z.string().max(max).meta({ description: 'Drittinhalt – nur als Text darstellen' });

/**
 * Bindung an die geprüfte redigierte Fassung („Das geht an die KI“, [TP5] 18.4 Nr. 3, Plan Welle 2 F N-F3): die
 * auslösenden Routen (Kommentar-Eingabe, Antworten, Neu formulieren, Floskel-Prüfung mit KI, Stimmbeispiele) tragen
 * optional `aiPreview`. Fehlt es, obwohl die Richtlinie `require` gilt oder der Detektor etwas gefunden hat, antworten
 * sie 409 `ai_preview_required`; eine abgelaufene Vorschau ergibt 409 `ai_preview_expired`. Zusätzliche Schwärzungen
 * verweisen auf Teil und Zeichenspanne der redigierten Fassung; der Originaltext wird nie mitgeschickt.
 * Bewusst ohne Komponenten-ID: das Schema wird von mehreren Modulen eingebettet.
 */
export const AiPreviewRef = z.strictObject({
  previewId: z.uuid(),
  extraRedactions: z
    .array(
      z
        .strictObject({
          part: z.int().min(0).max(99),
          start: z.int().min(0).max(29_999),
          end: z.int().min(1).max(30_000),
        })
        .refine((span) => span.end > span.start, { message: 'Das Ende liegt nach dem Anfang' }),
    )
    .max(200)
    .optional(),
});

// ------------------------------------------------------------------------------------------------
// Anmeldung
// ------------------------------------------------------------------------------------------------

const RETURN_TO_FORBIDDEN_ENCODINGS = /%5c|%2f%2f|%00|^\/%2f/i;
// biome-ignore lint/suspicious/noControlCharactersInRegex: Steuerzeichen sind hier der Prüfgegenstand.
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const OWN_ORIGIN_PROBE = 'https://socialfy.example';

function isSafeReturnTo(value: string): boolean {
  if (!value.startsWith('/') || value.startsWith('//')) {
    return false;
  }
  if (value.includes('\\') || CONTROL_CHARACTERS.test(value)) {
    return false;
  }
  if (RETURN_TO_FORBIDDEN_ENCODINGS.test(value)) {
    return false;
  }
  return (
    URL.canParse(value, OWN_ORIGIN_PROBE) &&
    new URL(value, OWN_ORIGIN_PROBE).origin === OWN_ORIGIN_PROBE
  );
}

/**
 * Rücksprungziel nach der Anmeldung (V12-05): genau ein führendes `/`, kein `\`, keine Steuerzeichen, keine
 * kodierten `%5c`/`%2f%2f`/`%00` (zusätzlich `/%2f…` direkt nach dem ersten Schrägstrich), Auflösung gegen die
 * eigene Origin ergibt dieselbe Origin. Der Handler (W1-21) wiederholt die Prüfung gegen die **konfigurierte**
 * Origin und fällt sonst auf `/` zurück.
 */
export const ReturnTo = z
  .string()
  .max(500)
  .refine(isSafeReturnTo, { message: 'Rücksprungziel ist kein interner Pfad' });

export const LoginQuery = z.strictObject({ returnTo: ReturnTo.optional() });

/**
 * Rückkehr von Entra. Entra sendet zusätzlich `session_state`, `iss` und `client_info` (und künftig weitere
 * Parameter): unbekannte Parameter werden ignoriert (Zod-Standard: entfernen), sonst wiese die Validierung den
 * echten Login mit 400 ab. `code`, `state`, `error` und `error_description` bleiben streng nach Typ und Länge
 * geprüft. Der Handler übergibt die unveränderte Adresse mit allen Parametern an den OIDC-Client, der `iss` selbst prüft.
 */
export const CallbackQuery = z.object({
  code: z.string().max(2048).optional(),
  state: z.string().max(2048).optional(),
  error: z.string().max(2048).optional(),
  error_description: z.string().max(2048).optional(),
});

/** Kopf der Weiterleitungen (302) von Login und Callback. */
export const RedirectHeaders = z.object({
  location: z
    .string()
    .max(2048)
    .meta({ description: 'Ziel der Weiterleitung (Entra bzw. interner Pfad, sonst /kein-zugang)' }),
});

// ------------------------------------------------------------------------------------------------
// Fehlerantworten mit Retry-After (Grenzen, V12-01)
// ------------------------------------------------------------------------------------------------

const RetryAfterHeaders = z.object({
  'retry-after': z
    .string()
    .regex(/^\d{1,7}$/)
    .meta({ description: 'Sekunden bis zur nächsten erlaubten Anfrage' }),
});

/**
 * Wie `problemResponses`, beschreibt bei `429` zusätzlich den Header `retry-after` (V12-01: Grenzen sind
 * 429 `rate_limited` mit `Retry-After`).
 */
export function errorResponses<const Codes extends readonly number[]>(codes: Codes) {
  const base = problemResponses(codes);
  const rateLimited = (base as Partial<Record<number, object>>)[429];
  return (
    rateLimited === undefined
      ? base
      : { ...base, 429: { ...rateLimited, headers: RetryAfterHeaders } }
  ) as ReturnType<typeof problemResponses<Codes>>;
}

// ------------------------------------------------------------------------------------------------
// Status und Sitzung
// ------------------------------------------------------------------------------------------------

export const HealthStatus = z.strictObject({ status: z.literal('ok') });
export const ReadyStatus = z.strictObject({ status: z.literal('ready') });

export const Me = z
  .strictObject({
    user: z.strictObject({ id: z.uuid(), displayName: z.string().min(1).max(120) }),
    mode: z.enum(['live', 'demo']),
    isDataSteward: z.boolean(),
    csrfToken: z
      .string()
      .min(32)
      .max(256)
      .regex(/^[A-Za-z0-9_-]+$/),
    demoAvailable: z.boolean(),
  })
  .meta({ id: 'Me' });

export const SessionModeBody = z.strictObject({ mode: z.enum(['live', 'demo']) });

// ------------------------------------------------------------------------------------------------
// Netzwerk
// ------------------------------------------------------------------------------------------------

export const Kpi = z
  .strictObject({
    value: z.int().nonnegative(),
    delta: z.int().nullable(),
    trend: z.array(z.strictObject({ weekStart: z.iso.date(), value: z.number() })).max(12),
  })
  .meta({ id: 'Kpi' });

/**
 * `audienceId` (NE-5, Plan Welle 2 E): alle Zahlen (Kontakte, Stärkeverteilung, enge, eingeschlafene und neu vernetzte
 * Personen) nur über die Mitglieder dieser eigenen Zielgruppe; die Zeitreihen bleiben gesamt. Eine fremde oder
 * unbekannte Zielgruppe ergibt 404.
 */
export const NetworkSummaryQuery = z.strictObject({
  range: z.enum(['30d', '90d', '12m', '24m']).default('12m'),
  audienceId: z.uuid().optional(),
});

export const NetworkSummary = z
  .strictObject({
    range: z.enum(['30d', '90d', '12m', '24m']),
    contacts: Kpi,
    historyStartsOn: z.iso.date().nullable(),
    newConnectionsByMonth: z
      .array(z.strictObject({ month: z.string().regex(/^\d{4}-\d{2}$/), value: z.int() }))
      .max(24),
    contactsTotal: z
      .array(
        z.strictObject({
          date: z.iso.date(),
          value: z.int(),
          coverage: z.enum(['complete', 'incomplete']),
        }),
      )
      .max(731),
    strengthDistribution: z
      .array(z.strictObject({ band: StrengthBand, count: z.int().nonnegative() }))
      .length(5),
    topOrganizations: z
      .array(z.strictObject({ name: thirdParty(160), count: z.int().nonnegative() }))
      .max(10),
    recentlyConnectedCount: z.int().nonnegative(),
    dormantCount: z.int().nonnegative(),
    closeCount: z.int().nonnegative(),
  })
  .meta({ id: 'NetworkSummary' });

// ------------------------------------------------------------------------------------------------
// Personen
// ------------------------------------------------------------------------------------------------

export const ListPersonsQuery = PageQuery.extend({
  q: z.string().min(1).max(200).optional(),
  connection: z.enum(['connected', 'invited', 'invited_by', 'none']).optional(),
  band: StrengthBand.optional(),
  inExchange: BoolQuery.optional(),
  sort: z.enum(['strength', 'last_interaction', 'name']).default('strength'),
});

export const PersonListItem = z
  .strictObject({
    id: z.uuid(),
    displayName: thirdParty(200),
    position: thirdParty(200).nullable(),
    organization: thirdParty(200).nullable(),
    connection: ConnectionState,
    band: StrengthBand,
    strength: z.int().min(0).max(100).nullable(),
    lastInteractionAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'PersonListItem' });

const Factor = z.number().min(0).max(1).nullable();

/** Faktoren der Stärke ([A] 2.5: Aktualität 0,35, Häufigkeit 0,25, Gegenseitigkeit 0,20, Initiative 0,10, Dauer 0,10). */
export const StrengthFactors = z
  .strictObject({
    recency: Factor,
    frequency: Factor,
    reciprocity: Factor,
    initiative: Factor,
    tenure: Factor,
  })
  .meta({ id: 'StrengthFactors' });

/**
 * Personendetail. Eingeschränkte Personen erscheinen nur hier (`restricted: true`, `strength.value = null`,
 * ohne Verlauf; V12-08/V12-10). `links.lastConversation` ist ein interner Pfad `/gespraeche/<uuid>` (RD-15).
 */
export const PersonDetail = z
  .strictObject({
    id: z.uuid(),
    displayName: thirdParty(200),
    position: thirdParty(200).nullable(),
    organization: thirdParty(200).nullable(),
    connection: z.strictObject({ state: ConnectionState, since: z.iso.date().nullable() }),
    strength: z.strictObject({
      value: z.int().min(0).max(100).nullable(),
      band: StrengthBand,
      coverage: z.number().min(0).max(1),
      factors: z.union([StrengthFactors, z.null()]),
      computedAt: z.iso.datetime().nullable(),
    }),
    identities: z
      .array(
        z.strictObject({
          id: z.uuid(),
          kind: z.enum(['profile_url', 'member_urn']),
          display: thirdParty(300),
          // (DB `IDENTITY_LINK_STATUS_VALUES`)
          status: z.enum(['confirmed', 'inferred']),
          rule: z.string().max(40),
          since: z.iso.datetime(),
        }),
      )
      .max(50),
    restricted: z.boolean(),
    art14: z.enum(['pending', 'provided', 'overdue', 'exempt_decision']).nullable(),
    links: z.strictObject({
      profile: LinkedInUrl.nullable(),
      lastConversation: InternalPath.nullable(),
    }),
    /**
     * Zweite Achse „Nähe“ neben der Stärke (JDG-8, Gesprächsurteile): Stufe und „warum“ (`du` = per Du, `met` =
     * Treffen/Gespräch, `helped` = gegenseitige Hilfe); ohne Urteil `null`.
     */
    closeness: z
      .strictObject({
        level: z.enum(['stranger', 'formal', 'familiar', 'personal']),
        reasons: z.array(z.enum(['du', 'met', 'helped'])).max(3),
      })
      .nullable(),
    /** Rolle bei der Softwareauswahl (JDG-8); nur bei sicherem Urteil, sonst `null`. */
    role: z.enum(['decider', 'co_decider', 'user']).nullable(),
  })
  .meta({ id: 'PersonDetail' });

export const TimelineItem = z
  .strictObject({
    id: z.uuid(),
    kind: z.enum(['message', 'invitation', 'connection']),
    occurredAt: z.iso.datetime(),
    direction: z.enum(['inbound', 'outbound', 'none']),
    snippet: thirdParty(140).nullable(),
    conversationId: z.uuid().nullable(),
    /** Nachricht hinter dem Eintrag, z. B. als Nachweis für den Datenschutzhinweis; sonst `null`. */
    messageId: z.uuid().nullable(),
  })
  .meta({ id: 'TimelineItem' });

export const StrengthFeedbackBody = z.strictObject({
  // (DB `STRENGTH_FEEDBACK_VERDICT_VALUES`)
  verdict: z.enum(['too_high', 'too_low']),
  note: z.string().max(280).optional(),
});

export const MergePersonBody = z.strictObject({ intoPersonId: z.uuid() });

export const RestrictPersonBody = z.strictObject({
  reason: z.enum(['art18', 'art21', 'other']),
  note: z.string().max(280).optional(),
});

/** Bestätigungswort der Löschung (zusammengesetztes Ö, keine Unicode-Normalisierung). */
const ERASE_CONFIRMATION = 'LÖSCHEN';

/**
 * Löschung einer Person: `confirm` exakt, **genau eins** von `privacyRequestId` und `reason` (V12-07: ein
 * Grund legt eine neue Anfrage `kind = erasure` an, eine vorhandene Anfrage behält ihren Grund).
 */
export const ErasePersonBody = z
  .strictObject({
    confirm: z.literal(ERASE_CONFIRMATION),
    privacyRequestId: z.uuid().optional(),
    reason: z.string().min(10).max(500).optional(),
  })
  .refine((v) => (v.privacyRequestId === undefined) !== (v.reason === undefined), {
    message: 'Genau eins von privacyRequestId und reason ist anzugeben',
  });

/**
 * Auskunft nach Art. 15 als JSON-Dokument: fester Kopf plus die fünf Abschnitte von `PersonExportChunk`
 * (W1-13, `createPersonExport`); die Route streamt sie zusammengesetzt, höchstens 50 MB ([A] 5.12).
 */
export const PersonExport = z.strictObject({
  exportedAt: z.iso.datetime(),
  personId: z.uuid(),
  restricted: z.boolean(),
  purposes: z.array(z.enum(['Z1', 'Z2', 'Z3'])).max(3),
  retention: z.strictObject({ rawMonths: z.int(), inactiveRelationshipMonths: z.int() }),
  source: z.literal('linkedin_account_of_controller'),
  profiles: z.array(
    z.strictObject({
      profileId: z.uuid(),
      firstName: thirdParty(200).nullable(),
      lastName: thirdParty(200).nullable(),
      displayName: thirdParty(200),
      position: thirdParty(200).nullable(),
      organization: thirdParty(200).nullable(),
      observedAt: z.iso.datetime(),
    }),
  ),
  identities: z.array(
    z.strictObject({
      identityId: z.uuid(),
      kind: z.enum(['profile_url', 'member_urn', 'email', 'odoo_partner']),
      value: thirdParty(2000),
      status: z.enum(['confirmed', 'inferred']),
      rule: z.string().max(40),
      since: z.iso.datetime(),
    }),
  ),
  relationships: z.array(
    z.strictObject({
      relationshipId: z.uuid(),
      connection: ConnectionState,
      connectedOn: z.iso.date().nullable(),
      strength: z.int().min(0).max(100).nullable(),
      coverage: z.number().min(0).max(1),
      factors: z
        .strictObject({
          recency: Factor,
          frequency: Factor,
          reciprocity: Factor,
          initiative: Factor,
          duration: Factor,
        })
        .nullable(),
      firstInteractionAt: z.iso.datetime().nullable(),
      lastInteractionAt: z.iso.datetime().nullable(),
    }),
  ),
  messages: z.array(
    z.strictObject({
      messageId: z.uuid(),
      conversationKind: z.enum(['one_to_one', 'group', 'unknown']),
      sentAt: z.iso.datetime(),
      direction: z.enum(['from_person', 'to_person']),
      text: thirdParty(8000).nullable(),
      edited: z.boolean(),
      deleted: z.boolean(),
    }),
  ),
  interactions: z.array(
    z.strictObject({
      interactionId: z.uuid(),
      type: z.string().max(40),
      occurredAt: z.iso.datetime(),
      objectRef: z.string().max(200).nullable(),
    }),
  ),
});

// ------------------------------------------------------------------------------------------------
// Gespräche und Zuordnungen
// ------------------------------------------------------------------------------------------------

export const ListConversationsQuery = PageQuery.extend({ personId: z.uuid().optional() });

/**
 * Nachrichten: `limit` 1–50 (Standard 50), `cursor` läuft rückwärts (ersetzt `before`, RD-15). Muster und
 * Standardwert (als Zeichenkette) stehen so auch im OpenAPI-Dokument.
 */
export const ListMessagesQuery = z.strictObject({
  limit: z
    .string()
    .regex(/^([1-9]|[1-4]\d|50)$/, 'limit muss eine ganze Zahl von 1 bis 50 sein')
    .default('50')
    .transform(Number)
    .pipe(z.int().min(1).max(50)),
  cursor: CursorString.optional(),
});

export const ConversationListItem = z
  .strictObject({
    id: z.uuid(),
    title: thirdParty(200).nullable(),
    isGroup: z.boolean(),
    participants: z
      .array(z.strictObject({ personId: z.uuid().nullable(), displayName: thirdParty(200) }))
      .max(50),
    lastMessageAt: z.iso.datetime(),
    lastMessageDirection: z.enum(['inbound', 'outbound', 'unknown']),
    lastSnippet: thirdParty(140).nullable(),
  })
  .meta({ id: 'ConversationListItem' });

export const MessageItem = z
  .strictObject({
    id: z.uuid(),
    direction: z.enum(['outbound', 'inbound', 'unknown']),
    sentAt: z.iso.datetime(),
    text: thirdParty(8000).nullable(),
    edited: z.boolean(),
    deleted: z.boolean(),
    source: z.enum(['snapshot', 'changelog', 'both']),
    contentRevision: z.int().min(1),
  })
  .meta({ id: 'MessageItem' });

export const IdentityReview = z
  .strictObject({
    linkId: z.uuid(),
    candidate: PersonListItem,
    other: z.union([PersonListItem, z.null()]),
    counterpart: z.strictObject({
      identityKind: z.enum(['member_urn', 'profile_url']),
      evidence: z.enum(['message_author', 'invitation', 'shared_identity']),
      occurredAt: z.iso.datetime().nullable(),
    }),
    rule: z.enum(['R3c_single_foreign_url', 'R4_invitation_time']).optional(),
    observedAt: z.iso.datetime().optional(),
  })
  .meta({ id: 'IdentityReview' });

export const IdentityDecisionBody = z.strictObject({ decision: z.enum(['accept', 'reject']) });
export const UndoIdentityDecisionBody = z.strictObject({ decisionId: z.uuid() });

export const PrepareActionBody = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('open_profile'), personId: z.uuid() }),
  z.strictObject({ kind: z.literal('open_conversation'), conversationId: z.uuid() }),
]);

/** `url = null` im Demo-Modus; der Hinweistext kommt aus dem Wörterbuch der Weboberfläche. */
export const PreparedAction = z.strictObject({
  url: LinkedInUrl.nullable(),
  hint: z.string().max(200).nullable(),
});

// ------------------------------------------------------------------------------------------------
// Konten und Abrufe
// ------------------------------------------------------------------------------------------------

/**
 * Konto mit Zustand (Form von `AccountStatusView`/`AccountView` aus W1-13, ohne `network`/`capabilities`);
 * **nie** Token oder Chiffrat.
 */
export const AccountStatus = z
  .strictObject({
    id: z.uuid(),
    connectorId: z.string().max(40),
    // (DB `CONNECTED_ACCOUNT_STATUS_VALUES`)
    status: z.enum([
      'pending',
      'active',
      'auth_failed',
      'token_expired',
      'consent_revoked',
      'paused',
      'error',
    ]),
    tokenState: z.enum(['valid', 'expiring', 'expired', 'unknown_expiry', 'missing']),
    tokenExpiresAt: z.iso.datetime().nullable(),
    consentSince: z.iso.datetime().nullable(),
    // (DB `TOKEN_BINDING_VALUES`)
    binding: z.enum(['unbound', 'bound', 'mismatch']),
    privacyAckAt: z.iso.datetime().nullable(),
    // Prüfung der eigenen Profiladresse ([A] 3.3.3); nie die Adresse selbst
    // Prüfung der eigenen Profiladresse ([A] 3.3.3) als Union der zulässigen Zustände; nie die Adresse selbst.
    // `check`: Ergebnis der letzten erfolglosen Prüfung (DB `SELF_URL_CHECK_RESULT_VALUES` ohne `verified`)
    selfProfile: z.union([
      z.strictObject({
        state: z.literal('verified'),
        verifiedAt: z.iso.datetime(),
        check: z.null(),
        sharePercent: z.null(),
      }),
      z.strictObject({
        state: z.literal('missing'),
        verifiedAt: z.null(),
        check: z.null(),
        sharePercent: z.null(),
      }),
      z.strictObject({
        state: z.literal('unverified'),
        verifiedAt: z.null(),
        check: z.enum(['below_threshold', 'ambiguous']),
        sharePercent: z.number().min(0).max(100),
      }),
      z.strictObject({
        state: z.literal('unverified'),
        verifiedAt: z.null(),
        check: z.literal('no_data').nullable(),
        sharePercent: z.null(),
      }),
    ]),
    streams: z
      .array(
        z.strictObject({
          stream: z.string().max(60),
          lastCompleteAt: z.iso.datetime().nullable(),
          nextDueAt: z.iso.datetime().nullable(),
          // (DB `ACCOUNT_STREAM_PAUSED_REASON_VALUES`)
          pausedReason: z
            .enum([
              'auth',
              'permanent',
              'protocol',
              'user',
              'unbound_token',
              'account_mismatch',
              'replay',
              'privacy_ack',
            ])
            .nullable(),
        }),
      )
      .max(20),
  })
  .meta({ id: 'AccountStatus' });

const TOKEN = z.string().min(20).max(4096).meta({
  description: 'LinkedIn-Zugriffstoken – wird nie geloggt und nie zurückgegeben',
  writeOnly: true,
});

export const ConnectLinkedIn = z.strictObject({
  token: TOKEN,
  tokenIssuedAt: z.iso.datetime().optional(),
  selfProfileUrl: z.url().regex(/^https:\/\/(www\.)?linkedin\.com\/in\/[^/?#]+\/?$/),
});

export const RenewTokenBody = z.strictObject({
  token: TOKEN,
  tokenIssuedAt: z.iso.datetime().optional(),
});

const STREAM_KEYS = ['changelog', 'profile', 'connections', 'invitations', 'inbox', 'all'] as const;
/** Abrufströme; `all` steht nur allein (entspricht `RefreshJobParams.streams`, V12-06). */
export const StreamKey = z.enum(STREAM_KEYS);

export const RefreshAccountBody = z.strictObject({
  streams: z
    .array(StreamKey)
    .min(1)
    .max(6)
    .refine((keys) => new Set(keys).size === keys.length, { message: 'Ströme sind eindeutig' })
    .refine((keys) => !keys.includes('all') || keys.length === 1, {
      message: '"all" steht nur allein',
    })
    .optional(),
});

export const DisconnectQuery = z.strictObject({ deleteData: BoolQuery });

export const ListFetchRunsQuery = PageQuery.extend({
  limit: z
    .string()
    .regex(/^([1-9]\d?|100)$/, 'limit muss eine ganze Zahl von 1 bis 100 sein')
    .default('50')
    .transform(Number)
    .pipe(z.int().min(1).max(MAX_PAGE_LIMIT)),
});

export const FetchRunItem = z.strictObject({
  id: z.uuid(),
  stream: z.string().max(60),
  // (DB `FETCH_RUN_TRIGGER_VALUES`)
  trigger: z.enum(['schedule', 'manual', 'bootstrap', 'retry']),
  // (DB `FETCH_RUN_STATUS_VALUES`)
  status: z.enum(['running', 'complete', 'partial', 'failed']),
  // (DB `FETCH_RUN_ERROR_CLASS_VALUES`)
  errorClass: z
    .enum([
      'auth',
      'rate_limited',
      'transient',
      'saturated',
      'protocol',
      'permanent',
      'aborted',
      'not_ready',
      'account_mismatch',
      'page_limit',
    ])
    .nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  newRecords: z.int().nonnegative(),
});

/** Datenlücke; den Klartext erzeugt das Wörterbuch aus `reason`. */
export const DataGapItem = z.strictObject({
  id: z.uuid(),
  from: z.iso.datetime(),
  to: z.iso.datetime().nullable(),
  // (DB `DATA_GAP_REASON_VALUES`)
  reason: z.enum(['saturated', 'lookback_exceeded', 'snapshot_incomplete', 'possible_unordered']),
  acknowledgedAt: z.iso.datetime().nullable(),
});

/**
 * Aufbereitungsstand der Rohdaten des Benutzers (nur Zählungen, keine Inhalte): `processed` enthält ignorierte und
 * zurückgestellte Einträge, `pending` die noch offenen, `failed` die mit Fehler. `active`: es wird noch gearbeitet
 * (offene Einträge, laufender Wiederholungsauftrag oder noch nicht aufbereiteter Posteingangslauf).
 */
export const ProcessingProgress = z.strictObject({
  active: z.boolean(),
  total: z.number().int().min(0),
  processed: z.number().int().min(0),
  pending: z.number().int().min(0),
  failed: z.number().int().min(0),
});

// ------------------------------------------------------------------------------------------------
// Was passiert gerade (Streifen auf jeder Seite)
// ------------------------------------------------------------------------------------------------

/** Art eines Vorgangs: Zielgruppen-Bewertung, Websuche, Bewertung eingefügter Treffer, Aufbereitung der Daten. */
export const ACTIVITY_KINDS = [
  'audience_scoring',
  'web_search',
  'paste_assess',
  'processing',
] as const;

/**
 * Probleme, die der Streifen im Klartext zeigt (die Sätze stehen im Wörterbuch der Oberfläche):
 * nicht eingerichtet bzw. nicht freigeschaltet, Pause bis zur nächsten Tagesgrenze, aufgebrauchtes Budget und Fehler der
 * letzten 24 Stunden nach Fehlerklasse zusammengefasst.
 */
export const ACTIVITY_PROBLEM_CODES = [
  'ai_not_configured',
  'judgment_not_configured',
  'judgment_not_released',
  'maps_not_configured',
  'audience_paused',
  'checks_paused',
  'budget_exhausted',
  'service_unreachable',
  'service_login_failed',
  'judgments_failed',
] as const;

const ActivityCount = z.int().nonnegative();
/** Name einer eigenen Zielgruppe (vom Benutzer vergeben); sonst `null`. Nie Daten Dritter. */
const ActivityLabel = z.string().max(80).nullable();

export const ActivityRun = z.strictObject({
  kind: z.enum(ACTIVITY_KINDS),
  /** Eigene Zielgruppe bzw. eigener Lauf; bei der Aufbereitung `null`. */
  refId: z.uuid().nullable(),
  label: ActivityLabel,
  /** Erledigt von allen; `null` = Stand unbekannt. */
  progress: z.strictObject({ done: ActivityCount, total: ActivityCount }).nullable(),
  /** Schritt eines Laufs (Websuche, Bewertung eingefügter Treffer); sonst `null`. */
  step: z.enum(['queued', 'searching', 'structuring', 'ranking']).nullable(),
});

export const ActivityRecent = z.strictObject({
  kind: z.enum(ACTIVITY_KINDS),
  refId: z.uuid().nullable(),
  label: ActivityLabel,
  outcome: z.enum(['done', 'partial', 'failed', 'cancelled', 'refused']),
  finishedAt: z.iso.datetime(),
  /** Zähler der Zusammenfassung (nur Anzahlen). */
  tally: z.strictObject({
    found: ActivityCount.optional(),
    members: ActivityCount.optional(),
    candidates: ActivityCount.optional(),
    excluded: ActivityCount.optional(),
  }),
});

export const ActivityProblem = z.strictObject({
  code: z.enum(ACTIVITY_PROBLEM_CODES),
  /** Pausen: ab wann es von selbst weitergeht. */
  resumesAt: z.iso.datetime().nullable(),
  /** Anzahl (Fehler der letzten 24 Stunden, zurückgestellte Kontakte); sonst `null`. */
  count: ActivityCount.nullable(),
});

/** Zähler je Zielgruppe für den Vergleich vorher/nachher (Zusammenfassung nach einer Bewertung). */
export const ActivityAudienceTally = z.strictObject({
  audienceId: z.uuid(),
  members: ActivityCount,
  candidates: ActivityCount,
  excluded: ActivityCount,
  deferred: ActivityCount,
});

/**
 * „Was passiert gerade“: laufende Vorgänge, die letzten Ergebnisse (24 Stunden), Probleme und Zähler je Zielgruppe. Nur
 * Daten des angemeldeten Benutzers, nur Zähler, keine Personenbezüge.
 */
export const Activity = z.strictObject({
  running: z.array(ActivityRun).max(50),
  recent: z.array(ActivityRecent).max(10),
  problems: z.array(ActivityProblem).max(20),
  audiences: z.array(ActivityAudienceTally).max(100),
});

// ------------------------------------------------------------------------------------------------
// Erinnerungen, Vorgänge, Einstellungen
// ------------------------------------------------------------------------------------------------

/** Erinnerung; **keine Freitexte vom Server** (Texte stehen im Wörterbuch). */
export const Reminder = z
  .strictObject({
    id: z.uuid(),
    // (DB `REMINDER_KIND_VALUES`)
    kind: z.enum(REMINDER_KINDS),
    // (DB `REMINDER_LEVEL_VALUES`)
    severity: z.enum(['info', 'warning', 'critical']),
    params: z.record(z.string(), z.union([z.string().max(64), z.number(), z.null()])),
    createdAt: z.iso.datetime(),
    action: z.strictObject({ route: InternalPath }).nullable(),
  })
  .meta({ id: 'Reminder' });

export const BackgroundOperation = z.strictObject({
  id: z.uuid(),
  // (DB `BACKGROUND_JOB_KIND_VALUES`)
  kind: z.enum(['refresh', 'erase_person', 'disconnect', 'replay', 'rotate_keys', 'export']),
  // (DB `BACKGROUND_JOB_STATUS_VALUES`)
  status: z.enum(['queued', 'running', 'done', 'failed', 'cancelled']),
  phase: z.string().max(40).nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  result: z.record(z.string(), z.int()).nullable(),
  errorCode: z.string().max(64).nullable(),
});

const HH_MM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const ModuleName = z.string().regex(/^[a-z]+$/);
/**
 * Abschnittswerte in begrenzter Tiefe (Zeichenkette, Zahl, Wahrheitswert, `null`, Liste oder flache Zuordnung
 * davon): das rekursive `z.json()` lässt sich nicht als OpenAPI-Schema ausgeben (Stapelüberlauf im Generator).
 */
const SettingLeaf = z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]);
const SettingValue = z.union([
  SettingLeaf,
  z.array(SettingLeaf).max(200),
  z.record(z.string().max(60), SettingLeaf),
]);
const ModuleSettings = z.record(ModuleName, z.record(z.string().max(60), SettingValue));

const CoreSettings = z.strictObject({
  refreshIntervalMinutes: z.int().min(15).max(1440),
  fullSyncTime: HH_MM,
  reminderLeadDays: z.int().min(1).max(30),
});

/**
 * Einstellungen. Modulabschnitte validiert der Server über die `app_setting`-Registry aus `packages/config`;
 * Module veröffentlichen ihre Abschnittsschemas selbst (z. B. `CommSettings`, RD-14).
 */
export const Settings = z
  .strictObject({ core: CoreSettings, modules: ModuleSettings })
  .meta({ id: 'Settings' });

/** Teilaktualisierung; letzte Schreibung gewinnt (`SettingsStore` kennt keine Versionen), Änderungen werden auditiert. */
export const SettingsUpdate = z
  .strictObject({
    core: CoreSettings.partial()
      .refine((core) => Object.keys(core).length > 0, {
        message: 'core braucht mindestens ein Feld',
      })
      .optional(),
    modules: ModuleSettings.optional(),
  })
  .refine((v) => v.core !== undefined || v.modules !== undefined, {
    message: 'Mindestens ein Feld ist anzugeben',
  });

// ------------------------------------------------------------------------------------------------
// Betroffenenanfragen
// ------------------------------------------------------------------------------------------------

const PrivacyKind = z.enum(['access', 'erasure', 'objection', 'restriction', 'rectification']); // (DB `PRIVACY_REQUEST_KIND_VALUES`)
const PrivacyStatus = z.enum(['open', 'done', 'rejected', 'extended']); // (DB `PRIVACY_REQUEST_STATUS_VALUES`)
const PrivacyChannel = z.enum(['email', 'linkedin', 'letter', 'other']); // (DB `PRIVACY_REQUEST_CHANNEL_VALUES`)

export const ListPrivacyRequestsQuery = PageQuery.extend({ status: PrivacyStatus.optional() });

export const PrivacyRequest = z
  .strictObject({
    id: z.uuid(),
    kind: PrivacyKind,
    status: PrivacyStatus,
    channel: PrivacyChannel,
    personId: z.uuid().nullable(),
    receivedAt: z.iso.datetime(),
    dueAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
    note: z.string().max(500).nullable(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'PrivacyRequest' });

export const PrivacyRequestCreate = z.strictObject({
  kind: PrivacyKind,
  channel: PrivacyChannel,
  personId: z.uuid().optional(),
  receivedAt: z.iso.datetime(),
  note: z.string().max(500).optional(),
});

/** Optimistische Sperre über `updated_at`, weil die Tabelle keine Versionsspalte hat. */
export const PrivacyRequestPatch = z.strictObject({
  expectedUpdatedAt: z.iso.datetime(),
  status: PrivacyStatus.optional(),
  note: z.string().max(500).nullable().optional(),
});

// ------------------------------------------------------------------------------------------------
// Fehlergründe (`reason` der Problem-Antwort)
// ------------------------------------------------------------------------------------------------

export const CoreProblemReason = z.enum([
  'already_merged',
  'merge_self',
  'person_erased',
  'person_restricted',
  'data_steward_required',
  'demo_only',
  'confirmation_mismatch',
  'token_rejected',
  'account_mismatch',
  'privacy_ack_missing',
  'art14_strategy_undecided',
  'limit_reached',
  'version_conflict',
  'decision_superseded',
]);

/** Komponenten-IDs, die dieses Modul vergibt (Plan D 3.8, `core`). */
export const OWN_COMPONENT_IDS = [
  'AccountStatus',
  'ConnectionState',
  'ConversationListItem',
  'IdentityReview',
  'Kpi',
  'Me',
  'MessageItem',
  'NetworkSummary',
  'PersonDetail',
  'PersonListItem',
  'PrivacyRequest',
  'Reminder',
  'Settings',
  'StrengthBand',
  'StrengthFactors',
  'TimelineItem',
] as const;
